import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MockAdCreative from "./MockAdCreative";

export default function SampleAnalysisSection() {
  const [activeTab, setActiveTab] = useState("original");

  return (
    <section className="py-24" data-testid="sample-analysis-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
            See AdBit <span className="gradient-text">in Action</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-4xl mx-auto"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-100 p-1 rounded-xl w-full max-w-md mx-auto grid grid-cols-3 h-auto" data-testid="analysis-tabs">
              <TabsTrigger
                value="original"
                data-testid="tab-original"
                className={`rounded-lg py-2.5 text-sm font-medium transition-all ${
                  activeTab === "original"
                    ? "bg-gradient-to-r from-orange-500 to-fuchsia-500 text-white shadow-md"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Original Ad
              </TabsTrigger>
              <TabsTrigger
                value="heatmap"
                data-testid="tab-heatmap"
                className={`rounded-lg py-2.5 text-sm font-medium transition-all ${
                  activeTab === "heatmap"
                    ? "bg-gradient-to-r from-orange-500 to-fuchsia-500 text-white shadow-md"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Heatmap
              </TabsTrigger>
              <TabsTrigger
                value="report"
                data-testid="tab-report"
                className={`rounded-lg py-2.5 text-sm font-medium transition-all ${
                  activeTab === "report"
                    ? "bg-gradient-to-r from-orange-500 to-fuchsia-500 text-white shadow-md"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Report
              </TabsTrigger>
            </TabsList>

            <div className="mt-8 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg shadow-slate-200/50">
              <TabsContent value="original" className="m-0">
                <OriginalAdTab />
              </TabsContent>
              <TabsContent value="heatmap" className="m-0">
                <HeatmapTab />
              </TabsContent>
              <TabsContent value="report" className="m-0">
                <ReportTab />
              </TabsContent>
            </div>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
}

function OriginalAdTab() {
  return (
    <div className="p-6 md:p-10" data-testid="tab-content-original">
      <div className="max-w-lg mx-auto">
        <MockAdCreative showHeatmap={false} size="lg" className="shadow-xl" />
      </div>
    </div>
  );
}

function HeatmapTab() {
  return (
    <div className="p-6 md:p-10" data-testid="tab-content-heatmap">
      <div className="max-w-lg mx-auto">
        <MockAdCreative showHeatmap={true} size="lg" className="shadow-xl" />
        {/* Legend */}
        <div className="mt-4 flex items-center justify-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-red-500" />
            <span className="text-xs text-slate-500">High attention</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-amber-500" />
            <span className="text-xs text-slate-500">Medium</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span className="text-xs text-slate-500">Low</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportTab() {
  const dimensions = [
    { label: "Attention", value: 87, color: "bg-emerald-500" },
    { label: "Cognitive Clarity", value: 72, color: "bg-amber-500" },
    { label: "Emotional Connection", value: 81, color: "bg-emerald-500" },
    { label: "Brand Visibility", value: 62, color: "bg-amber-500" },
    { label: "Memorability", value: 75, color: "bg-emerald-500" },
    { label: "CTA Effectiveness", value: 90, color: "bg-emerald-500" },
  ];

  const recommendations = [
    "Move logo to lower-right zone to increase brand recall by ~25%",
    "Increase CTA button size by 15% for better visibility",
    "Reduce text density in the center area to improve cognitive clarity",
  ];

  return (
    <div className="p-6 md:p-8" data-testid="tab-content-report">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Scores */}
        <div>
          {/* Overall score */}
          <div className="bg-gradient-to-br from-orange-500 to-fuchsia-500 rounded-xl p-5 text-white mb-4">
            <p className="text-sm font-medium text-orange-100 mb-1">Ad Neuro Score</p>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>78</span>
              <span className="text-lg text-orange-200 mb-1">/100</span>
            </div>
            <p className="text-xs text-orange-200 mt-1">Above average performance predicted</p>
          </div>

          {/* Dimension scores */}
          <div className="space-y-3">
            {dimensions.map((d) => (
              <div key={d.label} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-32 flex-shrink-0">{d.label}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${d.color}`} style={{ width: `${d.value}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-700 w-8 text-right" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {d.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Recommendations */}
        <div>
          <h4 className="text-sm font-bold text-slate-700 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>AI Recommendations</h4>
          <div className="space-y-3">
            {recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{i + 1}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{rec}</p>
              </div>
            ))}
          </div>

          {/* Quick stats */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
              <p className="text-[10px] text-emerald-600 font-medium">Engagement</p>
              <p className="text-lg font-bold text-emerald-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>High</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
              <p className="text-[10px] text-amber-600 font-medium">Brand Visibility</p>
              <p className="text-lg font-bold text-amber-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>62%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
